/**
 * Timing for the animation that plays when a cell is cleared: a short "shot"
 * flies from the block on the track to the cell, then the cell pops.
 *
 * This file only works out when each animation starts and how far along it
 * is; renderer.ts does the actual drawing. It reads the game's event log
 * (`state.events`) to find out which cells were cleared, and never changes
 * the game.
 */
import type { Color, GameState, RetiredBlock } from "./state";
import { slotCoords } from "./track";

type Point = { x: number; y: number };

/** One cleared cell's animation: the shot from the runner to the cell, then the pop. */
export interface Pop {
	/** Which cell was cleared (its index in the grid's flat cell list). */
	index: number;
	color: Color;
	/**
	 * Where the shot starts (the runner's track slot) and where it lands (the
	 * cell), in grid coordinates.
	 */
	from: Point;
	to: Point;
	/** When the clear was first noticed, in milliseconds (from `performance.now()`). */
	start: number;
	/** How long the shot takes to fly, in milliseconds. 0 means no shot. */
	travel: number;
}

/** An animation that's still running, and how far along its two parts are. */
export interface ActivePop {
	pop: Pop;
	/** How far the shot has flown, from 0 (just fired) to 1 (landed). */
	shot: number;
	/** How far the pop has played, from 0 to 1. Stays at 0 until the shot lands. */
	t: number;
}

/**
 * Keeps the list of running clear animations, turning new "clear" events
 * into timed animations.
 */
export class ClearEffects {
	private pops: Pop[] = [];
	private state: GameState | null = null;
	/**
	 * How many events have already been handled. Because the event log only ever
	 * grows, each frame only needs to look at entries past this point.
	 */
	private seen = 0;

	/**
	 * @param duration How long a pop lasts, in milliseconds.
	 * @param shotSpeed How fast shots fly, in grid cells per millisecond. 0 turns shots off.
	 */
	constructor(
		public duration: number,
		public shotSpeed: number,
	) {}

	/** Start animations for any cells cleared since the last call. Call once per frame. */
	sync(state: GameState, now: number): void {
		if (state !== this.state) {
			// A different state object means a new level has started, so forget the old
			// animations and start reading the new event log from the beginning.
			this.state = state;
			this.pops = [];
			this.seen = 0;
		}
		const { width, height } = state.grid;
		for (const event of state.events.slice(this.seen)) {
			if (event.type !== "clear") continue;
			const { index, color, slot } = event;
			const from = slotCoords(slot, width, height);
			const to = { x: index % width, y: Math.floor(index / width) };
			const distance = Math.hypot(to.x - from.x, to.y - from.y);
			const travel = this.shotSpeed > 0 ? distance / this.shotSpeed : 0;
			this.pops.push({ index, color, from, to, start: now, travel });
		}
		this.seen = state.events.length;
	}

	/**
	 * The animations still running at time `now`, with their progress. Finished
	 * ones are removed first.
	 */
	active(now: number): ActivePop[] {
		this.pops = this.pops.filter(
			(p) => now - p.start < p.travel + this.duration,
		);
		return this.pops.map((pop) => {
			const elapsed = now - pop.start;
			return {
				pop,
				shot: pop.travel === 0 ? 1 : Math.min(1, elapsed / pop.travel),
				t: Math.max(0, (elapsed - pop.travel) / this.duration),
			};
		});
	}
}

/**
 * One used-up block's pop as it leaves play: where it was (on the track or
 * in a tray slot, see `RetiredBlock`), and when it left, in milliseconds
 * (from `performance.now()`).
 */
export type BlockPop = RetiredBlock & { start: number };

/**
 * Keeps the list of running "block used up" pops, turning new "retire"
 * events into timed animations. Works like `ClearEffects`.
 */
export class RetireEffects {
	private pops: BlockPop[] = [];
	private state: GameState | null = null;
	private seen = 0;

	/** @param duration How long a pop lasts, in milliseconds. */
	constructor(public duration: number) {}

	/** Start pops for any blocks used up since the last call. Call once per frame. */
	sync(state: GameState, now: number): void {
		if (state !== this.state) {
			this.state = state;
			this.pops = [];
			this.seen = 0;
		}
		for (const event of state.events.slice(this.seen)) {
			if (event.type !== "retire") continue;
			for (const block of event.blocks)
				this.pops.push({ ...block, start: now });
		}
		this.seen = state.events.length;
	}

	/** The pops still running at time `now`, each with its progress `t` from 0 to 1. */
	active(now: number): { pop: BlockPop; t: number }[] {
		this.pops = this.pops.filter((p) => now - p.start < this.duration);
		return this.pops.map((pop) => ({
			pop,
			t: Math.max(0, (now - pop.start) / this.duration),
		}));
	}
}
